import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMantineColorScheme } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FullscreenModal from "../../../common/FullscreenModal/FullscreenModal";

const DISCRETIZATION = 1;

const createXAxisFormatter = (formatXAxis) => function formatTick() {
  return formatXAxis(this.value);
};

const createPointMouseOverHandler = (setHoveredPoint) => function handleMouseOver() {
  setHoveredPoint({ x: this.x, y: this.y });
};

const createToggleDataTableHandler = (chartRef) => () => {
  const chart = chartRef.current?.chart;
  const exporting = chart?.exporting;
  if (!chart || !exporting) return;

  if (exporting.isDataTableVisible) {
    chart.hideData();
  } else {
    chart.viewData();
  }
  exporting.options.menuItemDefinitions.toggleDataTable.textKey =
    exporting.isDataTableVisible ? "hideData" : "viewData";
};

export const createDistribution = ({
  average = 0,
  standardDeviation = 1,
  minimum,
  maximum,
  discretization = DISCRETIZATION,
  maxProbability = 100,
  pointCount,
}) => {
  const range = maximum - minimum;
  const binCount = pointCount
    ? Math.max(1, Number(pointCount))
    : Math.max(
        1,
        Math.ceil(range / Math.max(range * (discretization / 100), 1)),
      );
  const binWidth = range / binCount;
  const mean = Number(average) || 0;
  const deviation = Math.max(Number(standardDeviation) || 1, 0.0001);
  const probabilityCap = Number(maxProbability) || 100;
  const bins = [];
  let total = 0;

  for (let index = 0; index < binCount; index += 1) {
    const start = minimum + index * binWidth;
    const end = Math.min(maximum, start + binWidth);
    const midpoint = start + (end - start) / 2;
    const zScore = (midpoint - mean) / deviation;
    const rawProbability =
      midpoint < mean - 2 * deviation || midpoint > mean + 2 * deviation
        ? 0
        : Math.exp(-0.5 * zScore * zScore);

    total += rawProbability;
    bins.push({ midpoint, rawProbability });
  }

  const distributionPoints = [[minimum, 0]];
  const cappedProbabilities = bins.map(({ rawProbability }) =>
    total
      ? Math.min((rawProbability / total) * 100, probabilityCap)
      : 0,
  );
  const cappedTotal = cappedProbabilities.reduce(
    (sum, probability) => sum + probability,
    0,
  );
  bins.forEach(({ rawProbability }, index) => {
    const start = minimum + index * binWidth;
    const end = Math.min(maximum, start + binWidth);
    const probability = cappedTotal
      ? (cappedProbabilities[index] / cappedTotal) * 100
      : 0;
    distributionPoints.push([Number(start.toFixed(2)), probability]);
    distributionPoints.push([Number(end.toFixed(2)), probability]);
  });
  distributionPoints.push([maximum, 0]);
  return distributionPoints;
};

const DistributionChart = ({
  title,
  xAxisTitle,
  average,
  standardDeviation,
  minimum,
  maximum,
  discretization = DISCRETIZATION,
  maxProbability,
  pointCount,
  profileName,
  tickInterval,
  referenceLineValue,
  xAxisDisplayMax,
  formatXAxis = (value) => value,
  onDistributionChange,
  inModalInstance = false,
  distributionData,
  testId,
}) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === "dark";
  const backgroundColor = dark ? "#111111" : "#ffffff";
  const axisText = dark ? "#d5d7e0" : "#495057";
  const gridColor = dark ? "#2f2f2f" : "#e9ecef";
  const chartRef = useRef(null);
  const dataRef = useRef([]);
  const dragIndexRef = useRef(null);
  const contextMenuOpenRef = useRef(false);
  const skipSourceResetRef = useRef(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [fullscreenModal, setFullscreenModal] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState({ x: minimum, y: 0 });
  const [hoverLinePosition, setHoverLinePosition] = useState(null);
  const [hoverPointPosition, setHoverPointPosition] = useState(null);
  const [referenceLineHoverPosition, setReferenceLineHoverPosition] = useState(null);
  const [data, setData] = useState(() =>
    distributionData ||
      createDistribution({
        average,
        standardDeviation,
        minimum,
        maximum,
        discretization,
        maxProbability,
        pointCount,
      }),
  );

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    // A local edit (drag / set-to-zero / scale-to-100%) always wins over the
    // prop-driven reset below, regardless of whether the parent's profileName
    // prop has caught up to "custom" yet (avoids a reset race on local edits).
    if (skipSourceResetRef.current) {
      skipSourceResetRef.current = false;
      return;
    }
    if (distributionData) {
      setData(distributionData);
      return;
    }
    setData(
      createDistribution({
        average,
        standardDeviation,
        minimum,
        maximum,
        discretization,
        maxProbability,
        pointCount,
      }),
    );
  }, [
    average,
    standardDeviation,
    minimum,
    maximum,
    discretization,
    maxProbability,
    pointCount,
    profileName,
    distributionData,
  ]);

  const updateData = (nextData) => {
    skipSourceResetRef.current = true;
    dataRef.current = nextData;
    setData(nextData);
    onDistributionChange?.(nextData);
  };

  const resetZoom = () => {
    chartRef.current?.chart?.xAxis[0]?.setExtremes(
      minimum,
      xAxisDisplayMax ?? maximum,
    );
    setContextMenu(null);
  };

  useEffect(() => {
    const closeContextMenu = () => {
      contextMenuOpenRef.current = false;
      setContextMenu(null);
    };
    document.addEventListener("mousedown", closeContextMenu);
    return () => document.removeEventListener("mousedown", closeContextMenu);
  }, []);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return undefined;

    const container = chart.container;
    const getBinIndex = (xValue) => {
      const points = dataRef.current;
      const hasReferenceLine = Number.isFinite(Number(referenceLineValue));
      const maximumEditableX = Number(referenceLineValue);
      for (let index = 1; index < points.length - 1; index += 2) {
        const binEndsBeforeReferenceLine =
          !hasReferenceLine || points[index + 1][0] <= maximumEditableX;
        if (
          binEndsBeforeReferenceLine &&
          xValue >= points[index][0] &&
          xValue <= points[index + 1][0]
        ) {
          return index;
        }
      }
      return null;
    };
    const handleMouseDown = (event) => {
      if (event.button !== 0) return;
      if (
        event.target?.closest?.(
          ".highcharts-button, .highcharts-button-box, .highcharts-contextmenu",
        )
      ) {
        return;
      }
      const pointer = chart.pointer.normalize(event);
      const withinPlot =
        pointer.chartX >= chart.plotLeft &&
        pointer.chartX <= chart.plotLeft + chart.plotWidth &&
        pointer.chartY >= chart.plotTop &&
        pointer.chartY <= chart.plotTop + chart.plotHeight;
      if (!withinPlot) return;

      const xAxis = chart.xAxis?.[0];
      if (!xAxis) return;
      const xValue = xAxis.toValue(pointer.chartX);
      const binIndex = getBinIndex(xValue);
      if (binIndex === null) return;

      dragIndexRef.current = binIndex;
      container.style.cursor = "ns-resize";
      event.preventDefault();
    };
    const handleMouseMove = (event) => {
      if (contextMenuOpenRef.current) return;
      if (
        event.target?.closest?.(
          ".highcharts-button, .highcharts-button-box, .highcharts-contextmenu",
        )
      ) {
        setHoverLinePosition(null);
        setHoverPointPosition(null);
        setReferenceLineHoverPosition(null);
        return;
      }
      const pointer = chart.pointer.normalize(event);
      const edgeTolerance = 8;
      const withinPlot =
        pointer.chartX >= chart.plotLeft - edgeTolerance &&
        pointer.chartX <= chart.plotLeft + chart.plotWidth + edgeTolerance &&
        pointer.chartY >= chart.plotTop &&
        pointer.chartY <= chart.plotTop + chart.plotHeight;
      if (!withinPlot) {
        setHoverLinePosition(null);
        setHoverPointPosition(null);
        setReferenceLineHoverPosition(null);
        return;
      }

      const xAxis = chart.xAxis?.[0];
      if (!xAxis) return;
      const referenceValue = Number(referenceLineValue);
      const referenceLinePixel =
        Number.isFinite(referenceValue) &&
        referenceValue >= xAxis.min &&
        referenceValue <= xAxis.max
          ? xAxis.toPixels(referenceValue)
          : null;
      setReferenceLineHoverPosition(
        referenceLinePixel !== null &&
          Math.abs(pointer.chartX - referenceLinePixel) <= edgeTolerance
          ? chart.container.offsetLeft + referenceLinePixel
          : null,
      );
      const point = chart.series?.[0]?.searchPoint(pointer, true);
      if (!point) return;
      const xValue = point.x;
      setHoveredPoint({ x: point.x, y: point.y });
      setHoverLinePosition({
        left: chart.container.offsetLeft + chart.plotLeft + point.plotX,
        top: chart.container.offsetTop + chart.plotTop,
        height: chart.plotHeight,
        xValue,
      });
      setHoverPointPosition({
        left: chart.container.offsetLeft + chart.plotLeft + point.plotX,
        top: chart.container.offsetTop + chart.plotTop + point.plotY,
      });
      const binIndex = dragIndexRef.current;
      if (binIndex === null) return;

      const nextProbability = Math.max(
        0,
        Math.min(
          chart.yAxis[0].toValue(pointer.chartY),
          Number(maxProbability) || 100,
        ),
      );
      const points = dataRef.current.map(([value, probability]) => [
        value,
        probability,
      ]);
      const otherProbability = points.reduce(
        (total, [, probability], index) =>
          index % 2 === 1 && index !== binIndex
            ? total + (Number(probability) || 0)
            : total,
        0,
      );
      const remainingProbability = Math.max(0, 100 - otherProbability);
      const boundedProbability = Math.min(
        nextProbability,
        remainingProbability,
      );
      points[binIndex][1] = boundedProbability;
      points[binIndex + 1][1] = boundedProbability;
      dataRef.current = points;
      setData(points);
    };
    const handleMouseUp = () => {
      if (dragIndexRef.current !== null) {
        updateData(dataRef.current);
      }
      dragIndexRef.current = null;
      container.style.cursor = "";
    };

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      setHoverLinePosition(null);
      setHoverPointPosition(null);
      setReferenceLineHoverPosition(null);
    };
  }, [
    discretization,
    maxProbability,
    minimum,
    maximum,
    onDistributionChange,
    referenceLineValue,
  ]);

  const getProbabilityValues = (points) =>
    points
      .slice(1, -1)
      .filter((_, index) => index % 2 === 0)
      .map(([, value]) => Number(value) || 0);

  const normalizeProbabilitiesTo100 = (probabilities) => {
    if (!probabilities.length) return probabilities;

    const normalizedProbabilities = [...probabilities];
    const lastIndex = normalizedProbabilities.length - 1;
    let precedingTotal = normalizedProbabilities
      .slice(0, lastIndex)
      .reduce((sum, value) => sum + value, 0);

    if (precedingTotal >= 100) {
      const largestIndex = normalizedProbabilities
        .slice(0, lastIndex)
        .reduce(
          (selectedIndex, value, index, values) =>
            value > values[selectedIndex] ? index : selectedIndex,
          0,
        );
      normalizedProbabilities[largestIndex] = Math.max(
        0,
        normalizedProbabilities[largestIndex] - (precedingTotal - 100) - 1e-10,
      );
      precedingTotal = normalizedProbabilities
        .slice(0, lastIndex)
        .reduce((sum, value) => sum + value, 0);
    }

    normalizedProbabilities[lastIndex] = 100 - precedingTotal;
    return normalizedProbabilities;
  };

  const normalizeData = () => {
    const probabilityValues = getProbabilityValues(data);
    const total = probabilityValues.reduce(
      (sum, value) => sum + value,
      0,
    );
    const normalizedProbabilities = probabilityValues.map((value) =>
      total
        ? (value / total) * 100
        : probabilityValues.length
          ? 100 / probabilityValues.length
          : 0,
    );
    const safeProbabilities = normalizeProbabilitiesTo100(
      normalizedProbabilities,
    );
    const normalizedData = data.map(([value], index) => {
      if (index === 0 || index === data.length - 1) return [value, 0];
      return [value, safeProbabilities[Math.floor((index - 1) / 2)] || 0];
    });
    updateData(normalizedData);
  };

  const highestProbability = Math.max(
    0,
    ...data.map(([, probability]) => Number(probability) || 0),
  );
  const yAxisMax = highestProbability
    ? highestProbability * 1.2
    : 1;
  const totalProbability = getProbabilityValues(data).reduce(
    (sum, value) => sum + (Number(value) || 0),
    0,
  );
  const options = {
    chart: {
      type: "area",
      height: inModalInstance
        ? Math.max(500, (typeof window !== "undefined" ? window.innerHeight : 600) - 100)
        : 350,
      backgroundColor,
      spacing: [40, 30, 40, 50],
      zoomType: "x",
      events: {
        selection() {
          return dragIndexRef.current === null;
        },
      },
    },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    exporting: {
      enabled: false,
      buttons: {
        contextButton: {
          menuItems: [
            "printChart",
            "downloadPNG",
            "downloadJPEG",
            "downloadSVG",
            "downloadCSV",
            "downloadXLS",
            "toggleDataTable",
          ],
        },
      },
      menuItemDefinitions: {
        toggleDataTable: {
          textKey: "viewData",
          onclick: createToggleDataTableHandler(chartRef),
        },
      },
    },
    xAxis: {
      min: minimum,
      max: xAxisDisplayMax ?? maximum,
      tickInterval,
      gridLineWidth: 1,
      gridLineColor: gridColor,
      lineColor: gridColor,
      tickColor: gridColor,
      labels: {
        style: { color: axisText, fontSize: "10px", fontWeight: "500" },
        formatter: createXAxisFormatter(formatXAxis),
      },
      title: {
        text: xAxisTitle,
        style: { color: axisText, fontSize: "11px", fontWeight: "600" },
      },
      plotLines:
        Number.isFinite(Number(referenceLineValue)) &&
        Number(referenceLineValue) >= minimum &&
        Number(referenceLineValue) <= maximum
          ? [
              {
                color: "#ef4444",
                dashStyle: "Dash",
                width: 2,
                value: Number(referenceLineValue),
                zIndex: 5,
              },
            ]
          : [],
    },
    yAxis: {
      min: 0,
      max: yAxisMax,
      startOnTick: true,
      endOnTick: true,
      gridLineWidth: 1,
      gridLineColor: gridColor,
      lineColor: gridColor,
      tickColor: gridColor,
      labels: {
        style: { color: axisText, fontSize: "10px", fontWeight: "500" },
      },
      title: {
        text: t("fleet.chart.probabilityPercent"),
        style: { color: axisText, fontSize: "11px", fontWeight: "600" },
      },
    },
    tooltip: {
      enabled: false,
    },
    plotOptions: {
      area: {
        point: {
          events: {
            mouseOver: createPointMouseOverHandler(setHoveredPoint),
          },
        },
        marker: {
          enabled: true,
          radius: 3,
          fillColor: "transparent",
          lineColor: "transparent",
          states: {
            hover: {
              enabled: true,
              fillColor: "rgba(59, 130, 246, 0.3)",
              lineColor: "#3b82f6",
            },
          },
        },
        lineWidth: 2,
        fillColor: "rgba(59, 130, 246, 0.1)",
        fillOpacity: 1,
        step: "left",
      },
    },
    series: [
      {
        name: t("fleet.chart.probability"),
        color: "#3b82f6",
        data,
      },
    ],
  };

  return (
    <div
      data-testid={testId}
      onContextMenu={(event) => {
        event.preventDefault();
        contextMenuOpenRef.current = true;
        setHoverLinePosition(null);
        setHoverPointPosition(null);
        const bounds = event.currentTarget.getBoundingClientRect();
        setContextMenu({
          left: event.clientX - bounds.left,
          top: event.clientY - bounds.top,
        });
      }}
      style={{
        position: "relative",
        border: "1px solid #656668",
        borderRadius: "8px",
        padding: "16px",
        background: backgroundColor,
        boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
      }}
    >
      <div
        style={{
          minHeight: "42px",
          padding: "0 8px 8px",
          color: axisText,
          fontSize: "12px",
          lineHeight: 1.4,
          pointerEvents: "none",
        }}
      >
        <div>{`${xAxisTitle}: ${formatXAxis(hoveredPoint.x)}`}</div>
        <div>{`${t("fleet.chart.probability")}: ${Number(hoveredPoint.y).toFixed(2)}%`}</div>
      </div>
      {hoverLinePosition && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: `${hoverLinePosition.top}px`,
            height: `${hoverLinePosition.height}px`,
            left: `${hoverLinePosition.left}px`,
            borderLeft: "2px dotted #64748b",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
      )}
      {hoverPointPosition && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: `${hoverPointPosition.top}px`,
            left: `${hoverPointPosition.left}px`,
            width: "10px",
            height: "10px",
            border: "2px solid #ffffff",
            borderRadius: "50%",
            background: "#2563eb",
            boxShadow: "0 0 0 4px rgba(37, 99, 235, 0.25), 0 1px 4px rgba(15, 23, 42, 0.35)",
            transform: "translate(-50%, -50%)",
            zIndex: 3,
            pointerEvents: "none",
          }}
        />
      )}
      <div
        style={{
          position: "absolute",
          top: "16px",
          right: "16px",
          zIndex: 5,
          display: "flex",
          gap: "8px",
        }}
      >
        {!inModalInstance && (
          <button
            type="button"
            aria-label={t("fleet.chart.viewFullscreen")}
            title={t("fleet.chart.viewFullscreen")}
            onClick={() => setFullscreenModal(true)}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "4px",
              background: backgroundColor,
              color: axisText,
              padding: "4px 8px",
              fontSize: "16px",
              lineHeight: 1,
              cursor: "pointer",
            }}
          >
            ⛶
          </button>
        )}
        <button
          type="button"
          onClick={() => updateData(data.map(([value]) => [value, 0]))}
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: "4px",
            background: "#fff",
            color: "#475569",
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: 500,
            boxShadow: "0 1px 2px rgb(0 0 0 / 0.05)",
            cursor: "pointer",
          }}
        >
          {t("fleet.chart.setToZero")}
        </button>
        <button
          type="button"
          onClick={normalizeData}
          data-testid="distribution-chart-normalize-button"
          title={t("fleet.chart.scaleTo100")}
          aria-label={t("fleet.chart.scaleTo100")}
          style={{
            border: 0,
            borderRadius: "4px",
            background: "#3b82f6",
            color: "#fff",
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: 500,
            boxShadow: "0 1px 2px rgb(0 0 0 / 0.05)",
            cursor: "pointer",
          }}
        >
          {t("fleet.chart.scaleTo100")}
        </button>
      </div>
        {Number.isFinite(Number(referenceLineValue)) && (
          <div
            style={{
              position: "absolute",
              top: "16px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              padding: "8px",
              border: "1px solid #1e293b",
              borderRadius: "4px",
              background: backgroundColor,
              color: axisText,
              fontSize: "12px",
              lineHeight: 1.2,
              pointerEvents: "none",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "24px",
                  borderTop: "2px solid #3b82f6",
                }}
              />
              <span>{t("fleet.chart.distribution")}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "24px",
                  borderTop: "2px dashed #ef4444",
                }}
              />
              <span>{t("fleet.chart.maximumDrivingRange")}</span>
            </div>
          </div>
        )}
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
      {contextMenu && (
        <div
          role="menu"
          onMouseDown={(event) => event.stopPropagation()}
          style={{
            position: "absolute",
            left: contextMenu.left,
            top: contextMenu.top,
            zIndex: 6,
            minWidth: "120px",
            padding: "4px",
            border: "1px solid #e2e8f0",
            borderRadius: "4px",
            background: "#fff",
            boxShadow: "0 4px 12px rgb(15 23 42 / 0.15)",
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={resetZoom}
            style={{
              width: "100%",
              border: 0,
              background: "transparent",
              color: "#334155",
              padding: "6px 8px",
              textAlign: "left",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            {t("fleet.chart.resetZoom")}
          </button>
        </div>
      )}
      {referenceLineHoverPosition !== null && (
        <div
          role="tooltip"
          style={{
            position: "absolute",
            top: "48px",
            left: `${referenceLineHoverPosition - 8}px`,
            transform: "translateX(-100%)",
            zIndex: 4,
            padding: "6px 8px",
            border: "1px solid #ef4444",
            borderRadius: "4px",
            background: backgroundColor,
            color: axisText,
            fontSize: "12px",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {`${t("fleet.chart.maximumDrivingRange")}: ${formatXAxis(
            referenceLineValue,
          )}`}
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          padding: "0 8px",
          gap: "8px",
        }}
      >
        <span style={{ color: "#64748b", fontSize: "14px", fontWeight: 500 }}>
          {t("fleet.chart.totalProbabilityArea")}
        </span>
        <span
          style={{
            borderRadius: "6px",
            padding: "4px",
            fontSize: "14px",
            fontWeight: 700,
          }}
        >
          {totalProbability.toFixed(2)}%
        </span>
      </div>
      <FullscreenModal
        open={fullscreenModal && !inModalInstance}
        onClose={() => setFullscreenModal(false)}
        title={title}
      >
        <DistributionChart
          title={title}
          xAxisTitle={xAxisTitle}
          average={average}
          standardDeviation={standardDeviation}
          minimum={minimum}
          maximum={maximum}
          referenceLineValue={referenceLineValue}
          xAxisDisplayMax={xAxisDisplayMax}
          discretization={discretization}
          maxProbability={maxProbability}
          formatXAxis={formatXAxis}
          profileName={profileName}
          onDistributionChange={updateData}
          distributionData={data}
          inModalInstance
        />
      </FullscreenModal>
    </div>
  );
};

export default DistributionChart;